import type { FieldSchema, FormSchema } from '../types';
import type { FormService } from './formService';
import type { Question } from '../models/Question';
import { collectFieldSchemas, resolveDependantOptions } from './fieldSchemaService';

const SKIP_TYPES = new Set(['FileUpload', 'Fee', 'Callout', 'Message', 'ReadOnly']);

/**
 * True when the page was opened with `?demoPrefill=1` (or `true`).
 */
export function isDemoPrefillRequested(search: string = window.location.search): boolean {
  const value = new URLSearchParams(search).get('demoPrefill');
  return value === '1' || value === 'true';
}

/**
 * Fill every answerable question with a valid sample value so testers can
 * walk the form without typing. Choice fields take their first option;
 * dependant fields resolve against the parent answer already set.
 */
export function applyDemoPrefill(formService: FormService, schema: FormSchema): void {
  const fieldSchemas = collectFieldSchemas(schema);

  formService.getFormData().steps.forEach((step) => {
    step.sections.forEach((section) => {
      section.groups.forEach((group) => {
        group.questions.forEach((question) => {
          if (question.isDisplayOnly() || SKIP_TYPES.has(question.type)) {
            return;
          }

          const answer = demoAnswerFor(question, fieldSchemas[question.id], formService);
          if (answer !== undefined) {
            question.answer = answer;
          }
        });
      });
    });
  });
}

function firstOptionValue(
  fieldSchema: FieldSchema | undefined,
  formService: FormService
): string | undefined {
  const resolved = resolveDependantOptions(fieldSchema, formService);
  const value = resolved?.options?.[0]?.value;
  return value === undefined ? undefined : String(value);
}

function demoAnswerFor(
  question: Question,
  fieldSchema: FieldSchema | undefined,
  formService: FormService
): unknown {
  switch (question.type) {
    case 'Radio':
    case 'RadioList':
    case 'DropDown':
    case 'DropDownDependant':
    case 'RadioDependant':
      return firstOptionValue(fieldSchema, formService);
    case 'CheckList':
      return firstOptionValue(fieldSchema, formService);
    case 'CheckBox':
      return 'True';
    case 'Email':
      return 'demo.prefill@example.com';
    case 'Phone':
      return '021 555 0100';
    case 'Numeric':
      return '1';
    case 'Date':
    case 'DateText':
      return '15/03/2024';
    case 'MonthYear':
      return '03/2024';
    case 'Name':
      return 'Demo Prefill';
    case 'NZBN':
      return '9429037670009';
    case 'SplitQuantity':
      return [2, 1];
    case 'NotifiableEvents':
      return [1, 1, 0];
    case 'Location':
      return '123 Demo Street, Wellington 6011';
    case 'Text':
    case 'TextArea':
      return 'Demo prefill';
    default:
      return undefined;
  }
}
